import { State, VideoPlayerError } from '../state';
import { VideoBlockedError, VideoLoadingError, VideoError } from '@/scripts/store/modules/video/state/errors';
import { updateOverlay } from './manageVideoPlayerOverlays/setVideoPlayerErrorVideoOverlayBridge';

type AConstructorTypeOf<T> = new (...args: any[]) => T;

export interface SetVideoPlayerErrorPayload {
  error: VideoError | null;
  issuer?: AConstructorTypeOf<VideoError> | null;
  customMessage?: string;
}

const setError = (state: State, error: VideoPlayerError | null): void => {
  state.error = error;
  updateOverlay(state, error);
};

const createError = (error: VideoError | null, customMessage?: string): VideoPlayerError | null => (
  error == null
    ? null
    : ({
      cause: customMessage || error.message,
      message: error.message,
      error,
    })
);

const handleVideoBlockedErrorFlow = (state: State, payload: SetVideoPlayerErrorPayload): void => {
  // Attempting to clear error
  if (payload.error == null) {
    // Allow clearing only if the presenting error is a VideoBlockedError
    // We do not want to clear a presenting VideoLoadingError
    if (state.error?.error instanceof VideoBlockedError) {
      setError(state, null);
    }
    return;
  }

  setError(state, createError(payload.error, payload.customMessage));
};

const handleVideoLoadingError= (state: State, payload: SetVideoPlayerErrorPayload): void => {
  // Skip setting. VideoLoadingError should never override VideoBlockingError
  if (state.error?.error instanceof VideoBlockedError) return;

  setError(state, createError(payload.error, payload.customMessage));
};

export const setVideoPlayerError = (state: State, payload: SetVideoPlayerErrorPayload | null): void => {
  if (payload == null) {
    setError(state, null);
    return;
  }

  const { error, customMessage, issuer } = payload;

  // If original error is null, simply set state to the received payload
  if (state.error == null) {
    setError(state, createError(error, customMessage));
    return;
  }

  if (issuer === VideoBlockedError) {
    return handleVideoBlockedErrorFlow(state, payload);
  }

  if (issuer === VideoLoadingError) {
    return handleVideoLoadingError(state, payload);
  }

  // Simply set error if issuer is not handled above
  setError(state, createError(payload.error, payload.customMessage));
};
